<template>
  <div>
    <gcu-dialog v-model="isGcuDialogOpened" />
    <v-row no-gutters justify="center" align="center" class="py-6" v-if="isFetchingSignup">
      <v-progress-circular indeterminate color="accent" />
    </v-row>
    <v-form @submit.prevent.native="submit" v-else-if="hasSignup">
      <div class="text-center text-h6 pb-1">
        Bonjour {{ signup.userFirstname }} {{ signup.userLastname }} 👋
      </div>
      <div class="pt-2 pb-0 mb-6" v-if="hasError">
        <v-alert
          text
          :icon="false"
          type="error"
        >
          <div slot="prepend" class="pr-3">
            😱
          </div>
          {{ error }}
        </v-alert>
      </div>
      <div class="mt-5 pl-1 pb-1">
        <label class="font-weight-medium">
          Nom de votre entreprise
        </label>
      </div>
      <v-text-field
        v-model="companyName"
        outlined
        llabel="Nom de votre entreprise"
        persistent-placeholder
        hide-details
        background-color="rgba(234, 239, 247, 0.4)"
        :disabled="isSubmitting"
        autofocus
      />
      <div class="text-caption pl-1 pt-1">
        <v-icon small color="primary" v-if="!this.cleanCompanyName.length">more_horiz</v-icon>
        <v-icon small color="success" v-else-if="isCompanyNameLengthValid">done</v-icon>
        <v-icon small color="error" v-else>clear</v-icon>
        Fait au moins 3 caractères
      </div>
      <div class="mt-5 pl-1 pb-1">
        <label class="font-weight-medium">
          Votre URL Indeego
        </label>
      </div>
      <v-row no-gutters justify="center" align="center">
        <v-col>
          <v-text-field
            v-model="slug"
            outlined
            dense
            placeholder="mon-entreprise"
            class="slug-input"
            hide-details
            background-color="rgba(234, 239, 247, 0.4)"
            :disabled="isSubmitting"
            @change="isSlugManuallyUpdated = true"
            style="text-align: right;"
          />
        </v-col>
        <div class="pl-1 font-weight-medium">.indeego.fr</div>
      </v-row>
      <div class="text-caption pl-1 pt-1">
        <div>
          <v-icon small color="primary" v-if="!this.cleanSlug.length">more_horiz</v-icon>
          <v-icon small color="success" v-else-if="isSlugLengthValid">done</v-icon>
          <v-icon small color="error" v-else>clear</v-icon>
          Fait au moins 3 caractères
        </div>
        <div>
          <v-icon small color="primary" v-if="!this.cleanSlug.length">more_horiz</v-icon>
          <v-icon small color="success" v-else-if="isSlugValid">done</v-icon>
          <v-icon small color="error" v-else>clear</v-icon>
          Est valide
        </div>
        <div>
          <v-icon small color="primary" v-if="!this.cleanSlug.length || !isSlugValid">more_horiz</v-icon>
          <v-row no-gutters justify="center" align="center" style="width: 16px; display: inline-block;" v-else-if="isCheckingSlug">
            <v-progress-circular color="primary" size="11" width="2" indeterminate style="margin-left: 2px;"/>
          </v-row>
          <v-icon small color="success" v-else-if="isSlugAvailable">done</v-icon>
          <v-icon small color="error" v-else>clear</v-icon>
          Est disponible
        </div>
      </div>
      <v-row no-gutters justify="center" align="start" class="mt-7">
        <v-checkbox
          v-model="areLegaleInformationsAccepted"
          class="ma-0 pa-0"
          :ripple="false"
          hide-details
        />
        <v-col class="text-caption">
          J'ai lu et j'accepte les <a @click="isGcuDialogOpened = true">conditions générales de vente</a> d'Indeego
        </v-col>
      </v-row>
      <v-row no-gutters justify="center" align="center" class="mt-10 mb-10">
        <v-btn
          type="submit"
          rounded
          depressed
          color="primary"
          class="submit-btn"
          :loading="isSubmitting"
          :disabled="!canSubmit"
        >
          C'est parti 🚀
        </v-btn>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapMutations, mapState } from 'vuex';
import api from '@/api';
import GcuDialog from '@/components/GcuDialog.vue';

const DEFAULT_ERROR_MESSAGE = `Une erreur inconnue s'est produite, merci de réessayer`;

export default {
  data: () => ({
    companyName: ``,
    slug: ``,
    isFetchingSignup: false,
    isSlugManuallyUpdated: false,
    isCheckingSlug: true,
    isSlugAvailable: false,
    isSlugAvailabilityChecked: false,
    isSubmitting: false,
    areLegaleInformationsAccepted: false,
    error: ``,
    checkSlugId: 1,
    isGcuDialogOpened: false,
  }),

  computed: {
    ...mapState([
      `signupStep`,
    ]),
    ...mapState({
      storeSignup: `signup`,
    }),
    signup: {
      get() { return this.storeSignup; },
      set(value) { return this.SET_SIGNUP(value); },
    },
    token() {
      return this.$route.params?.token || null;
    },
    hasSignup() {
      return Boolean(this.signup?.token);
    },
    hasError() {
      return this.error?.length > 0;
    },
    cleanCompanyName() {
      return _.trim(this.companyName);
    },
    cleanSlug() {
      return _.trim(this.slug);
    },
    isCompanyNameLengthValid() {
      return this.cleanCompanyName.length >= 3;
    },
    isSlugLengthValid() {
      return this.cleanSlug.length >= 3;
    },
    isSlugValid() {
      const slugWithoutAllowedChars = this.cleanSlug
        .replace(/[A-Za-z]/g, ``)
        .replace(/[0-9]/g, ``)
        .replace(/-/g, ``);
      const slugWithoutDashes = this.cleanSlug.replace(/-/g, ``);

      return this.isSlugLengthValid
             && slugWithoutDashes.length
             && !slugWithoutAllowedChars.length
             && !this.cleanSlug.startsWith(`-`);
    },
    canSubmit() {
      return this.isCompanyNameLengthValid
             && this.isSlugValid
             && this.isSlugAvailable
             && this.areLegaleInformationsAccepted;
    },
  },

  methods: {
    ...mapMutations([
      `SET_SIGNUP`,
    ]),
    async fetchSignupIfNeeded() {
      if (this.signupStep === 2 && !this.hasSignup) {
        this.isFetchingSignup = true;
        const { data: signup } = await this.$api.get(`/signup/${this.token}`);
        this.signup = signup;
        try {
          window.$crisp.push([`set`, `user:nickname`, [`${signup.userFirstname} ${signup.userLastname}`]]);
          window.$crisp.push([`set`, `user:email`, [signup.userEmail]]);
        } catch (e) {} // eslint-disable-line
        this.isFetchingSignup = false;
      }
    },
    computeSlug() {
      if (!this.isSlugManuallyUpdated) {
        this.slug = _.kebabCase(this.cleanCompanyName.toLowerCase());
      }
    },
    checkSlug() {
      this.isSlugAvailable = false;
      this.checkSlugId += 1;
      const id = this.checkSlugId;
      clearTimeout(this.checkSlugTimeout);
      this.isCheckingSlug = true;
      this.checkSlugTimeout = setTimeout(async () => {
        let isSlugAvailable = false;
        try {
          const { data: { isAvailable } } = await this.$api.post(`/signup/${this.token}/check-slug`, { slug: this.cleanSlug });
          isSlugAvailable = isAvailable === true;
        } catch (e) {} // eslint-disable-line
        if (this.checkSlugId === id) {
          this.isSlugAvailable = isSlugAvailable;
          this.isCheckingSlug = false;
        }
      }, 500);
    },
    async submit() {
      this.isSubmitting = true;
      try {
        const { data: { success, error } } = await api.post(`/signup/${this.token}/space`, {
          name: this.cleanCompanyName,
          slug: this.cleanSlug,
        });
        if (success) {
          this.$router.replace(`/signup/${this.token}/check-email`);
        } else {
          this.error = error || DEFAULT_ERROR_MESSAGE;
        }
      } catch (e) {
        this.error = DEFAULT_ERROR_MESSAGE;
      }
      this.isSubmitting = false;
    },
  },

  mounted() {
    this.fetchSignupIfNeeded();
  },

  watch: {
    signupStep() {
      this.fetchSignupIfNeeded();
    },
    companyName() {
      this.computeSlug();
    },
    slug() {
      if (this.isSlugValid) {
        this.checkSlug();
      }
    },
  },

  components: {
    GcuDialog,
  },
};
</script>

<style lang="scss">
.slug-input {
  input {
    text-align: right !important;
  }
}
</style>
